import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, PencilSimple, X, Trash, CheckCircle, GearSix } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/lib/api";
import { COLOR_OPTIONS } from "./comercialConstants";

export const ComercialStatusManager = ({ statusTypes, onRefresh, token }) => {
  const [showForm, setShowForm] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(COLOR_OPTIONS[0].value);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editColor, setEditColor] = useState("");

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    const name = newName.trim() || newLabel.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    setCreating(true);
    try {
      const colorOpt = COLOR_OPTIONS.find(c => c.value === newColor);
      await axios.post(`${API}/comercial-status-types`, {
        name, label: newLabel.trim(), color: newColor, hex_color: colorOpt?.hex || "#64748B"
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Estatus creado");
      setNewLabel(""); setNewName(""); setShowForm(false);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear estatus"); }
    finally { setCreating(false); }
  };

  const handleUpdate = async (statusId) => {
    try {
      const colorOpt = COLOR_OPTIONS.find(c => c.value === editColor);
      await axios.put(`${API}/comercial-status-types/${statusId}`, {
        label: editLabel, color: editColor, hex_color: colorOpt?.hex || "#64748B"
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Estatus actualizado");
      setEditingId(null);
      onRefresh();
    } catch (e) { toast.error("Error al actualizar"); }
  };

  const handleDelete = async (statusId, statusName) => {
    if (!window.confirm(`Eliminar el estatus "${statusName}"?`)) return;
    try {
      await axios.delete(`${API}/comercial-status-types/${statusId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Estatus eliminado");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  return (
    <div className="border border-slate-200 bg-white p-4 mb-6" data-testid="status-manager">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><GearSix size={16} /> Gestionar Estatus Comerciales</h3>
        <Button variant="outline" size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-status-btn">
          <Plus size={14} className="mr-1" /> Nuevo Estatus
        </Button>
      </div>

      {showForm && (
        <div className="border border-blue-200 bg-blue-50 p-3 mb-3 space-y-2" data-testid="new-status-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Etiqueta *</Label>
              <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} className="input-sharp mt-1" placeholder="Ej: Prospecto Calificado" data-testid="new-status-label" />
            </div>
            <div>
              <Label className="text-xs">Clave (auto si vacio)</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="input-sharp mt-1" placeholder="prospecto_calificado" data-testid="new-status-name" />
            </div>
            <div>
              <Label className="text-xs">Color</Label>
              <Select value={newColor} onValueChange={setNewColor}>
                <SelectTrigger className="input-sharp mt-1" data-testid="new-status-color">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLOR_OPTIONS.map(c => (
                    <SelectItem key={c.value} value={c.value}>
                      <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: c.hex }} />{c.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button size="sm" className="btn-primary" onClick={handleCreate} disabled={creating || !newLabel.trim()} data-testid="submit-new-status">
              {creating ? "Creando..." : "Crear"}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-1">
        {statusTypes.map(st => (
          <div key={st.id} className="flex items-center justify-between p-2 border border-slate-100 bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`manage-status-${st.id}`}>
            {editingId === st.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="input-sharp w-48" data-testid="edit-status-label" />
                <Select value={editColor} onValueChange={setEditColor}>
                  <SelectTrigger className="input-sharp w-36" data-testid="edit-status-color">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLOR_OPTIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: c.hex }} />{c.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="btn-primary" onClick={() => handleUpdate(st.id)} data-testid="save-status-edit"><CheckCircle size={16} /></Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><X size={16} /></Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 text-xs font-medium border rounded ${st.color}`}>{st.label}</span>
                  <span className="text-xs text-slate-400 font-mono">{st.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700" onClick={() => { setEditingId(st.id); setEditLabel(st.label); setEditColor(st.color); }} data-testid={`edit-status-${st.id}`}>
                    <PencilSimple size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600" onClick={() => handleDelete(st.id, st.label)} data-testid={`delete-status-${st.id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
